import * as React from 'react'
import type { ColumnDef } from '@tanstack/react-table'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import type { DataTableFilterField } from '@/components/data-table/types'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import {
  mapChronicCondition,
  mapLabResult,
  type ClinicalDataEndpoint,
} from '@/features/clinical-data/api/api'
import { useClinicalDataQuery } from '@/features/clinical-data/hooks/useClinicalDataQuery'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import type { SchemaDefinition } from '@/lib/store/schema'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import {
  chronicConditionsColumns,
  labResultsColumns,
} from './ClinicalDataColumns'
import {
  chronicConditionsFilterFields,
  chronicConditionsSchema,
  labResultsFilterFields,
  labResultsSchema,
} from './clinicalDataTableSchema'
import type { ChronicConditionRecord, LabResultRecord } from '../types'

interface ClinicalDataConfig<T extends { id: string }> {
  tableId: string
  endpoint: ClinicalDataEndpoint
  entityLabel: string
  sortBy: string
  columns: ColumnDef<T>[]
  filterFields: DataTableFilterField<T>[]
  schema: { definition: SchemaDefinition }
  mapRow: (row: Record<string, unknown>) => T
}

function ClinicalDataContent<T extends { id: string }>({
  config,
}: {
  config: ClinicalDataConfig<T>
}) {
  const storeState = useFilterState()
  const { resetAllFilters } = useFilterActions()
  const filters = React.useMemo(() => {
    const ids = new Set(config.filterFields.map((field) => String(field.value)))
    return columnFiltersToFilterConditions(
      Object.entries(storeState ?? {})
        .filter(([key, value]) => {
          if (!ids.has(key) || value === null || value === undefined)
            return false
          if (Array.isArray(value)) return value.length > 0
          return String(value).trim().length > 0
        })
        .map(([id, value]) => ({ id, value }))
    )
  }, [config.filterFields, storeState])
  const query = useClinicalDataQuery({
    endpoint: config.endpoint,
    params: {
      limit: 50,
      sort_by: config.sortBy,
      sort_order: 'desc',
      filters: filters.length > 0 ? filters : undefined,
    },
    mapRow: config.mapRow,
  })
  const data = React.useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data]
  )
  const toolbarActions = React.useMemo(
    () => (
      <TabRefreshButton
        onRefresh={query.refetch}
        isFetching={query.isFetching}
      />
    ),
    [query.isFetching, query.refetch]
  )
  const getRowId = React.useCallback((row: T) => row.id, [])

  return (
    <DataTableInfinite<T, unknown>
      tableId={config.tableId}
      columns={config.columns}
      data={data}
      filterFields={config.filterFields}
      manualFiltering
      isError={query.isError}
      error={query.error}
      entityLabel={config.entityLabel}
      onClearFilters={resetAllFilters}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRows={query.data?.pages[0]?.pagination.total ?? undefined}
      totalRowsFetched={data.length}
      getRowId={getRowId}
      toolbarActions={toolbarActions}
      enableRowDetails={false}
      autoColumnWidth
    />
  )
}

function ClinicalDataList<T extends { id: string }>({
  config,
}: {
  config: ClinicalDataConfig<T>
}) {
  const adapter = useNuqsAdapter(config.schema.definition, {
    id: config.tableId,
    history: 'replace',
    namespaceUrlKeys: true,
  })
  return (
    <DataTableStoreProvider adapter={adapter}>
      <ClinicalDataContent config={config} />
    </DataTableStoreProvider>
  )
}

const LAB_RESULTS_CONFIG: ClinicalDataConfig<LabResultRecord> = {
  tableId: 'lab_results',
  endpoint: 'lab-results',
  entityLabel: 'lab results',
  sortBy: 'observed_at',
  columns: labResultsColumns,
  filterFields: labResultsFilterFields,
  schema: labResultsSchema,
  mapRow: mapLabResult,
}

const CHRONIC_CONDITIONS_CONFIG: ClinicalDataConfig<ChronicConditionRecord> = {
  tableId: 'chronic_conditions',
  endpoint: 'chronic-conditions',
  entityLabel: 'chronic conditions',
  sortBy: 'effective_date',
  columns: chronicConditionsColumns,
  filterFields: chronicConditionsFilterFields,
  schema: chronicConditionsSchema,
  mapRow: mapChronicCondition,
}

export function LabResultsList() {
  return <ClinicalDataList config={LAB_RESULTS_CONFIG} />
}

export function ChronicConditionsList() {
  return <ClinicalDataList config={CHRONIC_CONDITIONS_CONFIG} />
}
